import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  SvgImage,
  RadioInputDiv,
  Result,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
  RadioWrapper,
} from '../../../styles/global'
import { LeftIcon, RightIcon } from './styles'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function MansoryPanelBending() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const mansoryGradient = 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)'
  const [ftdNormal, setFtdNormal] = useState(0.125)
  const [ftdParallel, setFtdParallel] = useState(0.25)
  const [pfa, setPfa] = useState(0)
  const [h, setH] = useState('')
  const [l, setL] = useState('')
  const [wd, setWd] = useState(0)
  const [situation, setSituation] = useState('1')
  const [supports, setSupports] = useState(13)
  const [isFormValid, setIsFormValid] = useState(false)
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'mansory-panel-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFtdNormal(entry.ftdNormal)
      setFtdParallel(entry.ftdParallel)
      setPfa(entry.pfa)
      setH(entry.h)
      setL(entry.l)
      setWd(entry.wd)
      setSituation(entry.situation)
      setSupports(entry.supports)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (!['pfa', 'wd'].includes(name) && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.ftdNormal = validateField('ftdNormal', ftdNormal)
    newErrors.ftdParallel = validateField('ftdParallel', ftdParallel)
    newErrors.pfa = validateField('pfa', pfa)
    newErrors.h = validateField('h', h)
    newErrors.l = validateField('l', l)
    newErrors.wd = validateField('wd', wd)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [ftdNormal, ftdParallel, pfa, h, l, wd, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        ftdNormal,
        ftdParallel,
        pfa,
        h,
        l,
        wd,
        situation,
        supports,
      }

      setData(data)

      const response = await api.post('/mansory/panel-bending', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        ftdNormal,
        ftdParallel,
        pfa,
        h,
        l,
        wd,
        situation,
        supports,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      ftdNormal,
      ftdParallel,
      pfa,
      h,
      l,
      wd,
      isFormValid,
      situation,
      supports,
      data,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFtdNormal(jsonData.ftdNormal)
        setFtdParallel(jsonData.ftdParallel)
        setPfa(jsonData.pfa)
        setH(jsonData.h)
        setL(jsonData.l)
        setWd(jsonData.wd)
        setIsFormValid(jsonData.isFormValid)
        setSituation(jsonData.situation)
        setSupports(jsonData.supports)
        setData(jsonData.data)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o arquivo. Verifique o formato.')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
      errorMessage: 'Erro ao copiar os cálculos. Tente novamente.',
    })
  }

  const Case1 = () => {
    if (supports !== 1) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="300" y2="94.1589" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
      </SvgImage>
    )
  }
  const Case2 = () => {
    if (supports !== 2) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="302" y1="95.6589" x2="302" y2="295.659" stroke="black" strokeWidth="4" />
        <line x1="100" y1="94.1589" x2="300" y2="94.1589" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="303.809" y="95.6589" width="8.68214" height="200" fill="black" />
      </SvgImage>
    )
  }
  const Case3 = () => {
    if (supports !== 3) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="300" y2="94.1589" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="302.853" y="95.6589" width="8.68214" height="200" fill="black" />
        <rect x="91.3179" y="95.6589" width="8.68214" height="200" fill="black" />
      </SvgImage>
    )
  }
  const Case4 = () => {
    if (supports !== 4) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="300" y2="94.1589" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="302.853" y="95.6589" width="8.68214" height="200" fill="black" />
        <rect
          x="311.535"
          y="295.659"
          width="8.68214"
          height="220.217"
          transform="rotate(90 311.535 295.659)"
          fill="black"
        />
        <rect x="91.3178" y="95.6589" width="8.68214" height="200" fill="black" />
      </SvgImage>
    )
  }
  const Case5 = () => {
    if (supports !== 5) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
      </SvgImage>
    )
  }
  const Case6 = () => {
    if (supports !== 6) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="303.022" y="95.6589" width="8.68214" height="200" fill="black" />
      </SvgImage>
    )
  }
  const Case7 = () => {
    if (supports !== 7) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="303.022" y="95.6589" width="8.68214" height="200" fill="black" />
        <rect x="91.3178" y="95.6589" width="8.68214" height="200" fill="black" />
      </SvgImage>
    )
  }
  const Case8 = () => {
    if (supports !== 8) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="303.022" y="95.6589" width="8.68214" height="200" fill="black" />
        <rect x="91.3179" y="95.6589" width="8.68214" height="200" fill="black" />
        <rect
          x="311.704"
          y="295.659"
          width="8.68214"
          height="220.217"
          transform="rotate(90 311.704 295.659)"
          fill="black"
        />
      </SvgImage>
    )
  }
  const Case9 = () => {
    if (supports !== 9) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="303.022" y="95.6589" width="8.68214" height="200" fill="black" />
        <rect x="91.3179" y="95.6589" width="8.68214" height="200" fill="black" />
        <rect
          x="311.704"
          y="295.659"
          width="8.68214"
          height="220.217"
          transform="rotate(90 311.704 295.659)"
          fill="black"
        />
        <rect
          x="311.704"
          y="86.9768"
          width="8.68214"
          height="220.217"
          transform="rotate(90 311.704 86.9768)"
          fill="black"
        />
      </SvgImage>
    )
  }
  const Case10 = () => {
    if (supports !== 10) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
      </SvgImage>
    )
  }
  const Case11 = () => {
    if (supports !== 11) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="91.3179" y="92.668" width="8.68214" height="202.991" fill="black" />
      </SvgImage>
    )
  }
  const Case12 = () => {
    if (supports !== 12) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
        <rect x="91.3179" y="92.668" width="8.68214" height="202.991" fill="black" />
        <rect
          x="303.015"
          y="295.659"
          width="8.68214"
          height="211.697"
          transform="rotate(90 303.015 295.659)"
          fill="black"
        />
      </SvgImage>
    )
  }
  const Case13 = () => {
    if (supports !== 13) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
      </SvgImage>
    )
  }
  const Case14 = () => {
    if (supports !== 14) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
      </SvgImage>
    )
  }
  const Case15 = () => {
    if (supports !== 15) return null

    return (
      <SvgImage width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="101.5" y1="95.6589" x2="101.5" y2="295.659" stroke="black" strokeWidth="3" />
        <line x1="301.5" y1="95.6589" x2="301.5" y2="295.659" stroke="black" strokeWidth="3" strokeDasharray="10 10" />
        <line x1="100" y1="94.1589" x2="303.022" y2="94.1589" stroke="black" strokeWidth="3" />
        <line x1="100" y1="294.159" x2="300" y2="294.159" stroke="black" strokeWidth="3" />
      </SvgImage>
    )
  }

  return (
    <PageContainer>
      <PageHeader $gradient={mansoryGradient}>
        <div className="header-content">
          <BackButton to="/alvenaria">
            <FiArrowLeft /> Voltar para Alvenaria
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/alvenaria">Alvenaria</a>
            <span>/</span>
            <span>Esforços em Painéis</span>
          </div>
          <h1>🧱 Esforços em Painéis de Alvenaria Estrutural</h1>
          <p className="subtitle">
            Cálculo de momentos fletores em painéis de alvenaria estrutural sob carregamento distribuído
          </p>
          <div className="badges">
            <Badge>📋 NBR 16868-1:2020</Badge>
            <Badge>📐 Análise de Painéis</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard>
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta calcula os momentos fletores em painéis de alvenaria estrutural, considerando diferentes
              condições de contorno e tipos de apoio, conforme a NBR 16868-1:2020.
            </p>
            <p>
              O cálculo determina os momentos fletores nas direções paralela e normal ao plano do painel, levando em
              conta as características geométricas, carregamento aplicado e condições de apoio do elemento estrutural.
            </p>
            <div className="norms">
              <span>NBR 16868-1:2020</span>
              <span>Teoria de Placas</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>
                f <sub>td,normal</sub> (MPa)
              </span>
              <ValidatedInput
                name="ftdNormal"
                type="number"
                value={ftdNormal || ''}
                onChange={(event) => setFtdNormal(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>
                f <sub>td,paralela</sub> (MPa)
              </span>
              <ValidatedInput
                name="ftdParallel"
                type="number"
                value={ftdParallel || ''}
                onChange={(event) => setFtdParallel(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Altura (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Largura (cm)</span>
              <ValidatedInput
                name="l"
                type="number"
                value={l || ''}
                onChange={(event) => setL(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Carregamento Distribuído (kN/m²)</span>
              <ValidatedInput
                name="wd"
                type="number"
                value={wd}
                onChange={(event) => setWd(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>
                P<sub>f</sub>/A<sub>e</sub> (MPa)
              </span>
              <ValidatedInput
                name="pfa"
                type="number"
                value={pfa}
                onChange={(event) => setPfa(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <RadioInputDiv>
              <span>Situação</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="situation"
                    id="situation1"
                    value="1"
                    checked={situation === '1'}
                    onChange={(e) => {
                      setSituation(e.target.value)
                      setSupports(13)
                    }}
                  />
                  <span>Não armada</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="situation"
                    id="situation2"
                    value="2"
                    checked={situation === '2'}
                    onChange={(e) => {
                      setSituation(e.target.value)
                      setSupports(1)
                    }}
                  />
                  <span>Armada</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            <InputDiv>
              <div>
                <span style={{ minWidth: '50px' }}>Caso</span>
                <LeftIcon
                  onClick={() => {
                    if (situation == '1') setSupports((prev) => Math.max(prev - 1, 13))
                    else setSupports((prev) => Math.max(prev - 1, 1))
                  }}
                />
                <RightIcon
                  onClick={() => {
                    if (situation == '1') setSupports((prev) => Math.min(prev + 1, 15))
                    else setSupports((prev) => Math.min(prev + 1, 12))
                  }}
                />
              </div>
              <Case1 />
              <Case2 />
              <Case3 />
              <Case4 />
              <Case5 />
              <Case6 />
              <Case7 />
              <Case8 />
              <Case9 />
              <Case10 />
              <Case11 />
              <Case12 />
              <Case13 />
              <Case14 />
              <Case15 />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath math={`M_d,paralelo = ${result.mdParallel.toFixed(2)} \\, \\text{kN.m/m}`} />
                <BlockMath math={`M_d,normal = ${result.mdNormal.toFixed(2)} \\, \\text{kN.m/m}`} />
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Dados da tabela</h3>
              <MathDiv>
                <BlockMath math={`\\dfrac{P_f}{A_e} = ${result.pfaf} \\, MPa`} />
                <BlockMath math={`\\mu = ${result.muf}`} />
                <BlockMath math={`\\dfrac{h}{l} = \\dfrac{${data.h}}{${data.l} }= ${result.hl.toFixed(3)}`} />
              </MathDiv>
              <h3>Resposta</h3>
              <MathDiv>
                <BlockMath math={`\\beta_f = ${result.bf.toFixed(4)}`} />
                <BlockMath math={`w_d = ${data.wd} \\, kN/m^2`} />
                <BlockMath math={`M_d,paralelo = \\beta_f \\cdot w_d \\cdot l^2`} />
                <BlockMath
                  math={`M_d,paralelo = ${result.bf.toFixed(4)} \\cdot ${data.wd} \\cdot ${data.l / 100} ^2`}
                />
                <BlockMath math={`\\boxed{M_d,paralelo = ${result.mdParallel.toFixed(2)} \\, \\text{kN.m/m}} `} />
                <BlockMath math={`M_d,normal = \\mu \\cdot \\beta_f \\cdot w_d \\cdot l^2`} />
                <BlockMath
                  math={`M_d,normal = ${result.muf} \\cdot  ${result.bf.toFixed(4)} \\cdot ${data.wd} \\cdot ${data.l / 100} ^2`}
                />
                <BlockMath math={`\\boxed{M_d,normal = ${result.mdNormal.toFixed(2)} \\, \\text{kN.m/m}} `} />
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
